import { CloudActions, CostCenterServiceUrls } from '@portal/portal-core';
import { call, put, select } from 'redux-saga/effects';
import Lockr from 'utils/local-store';
import { get } from 'lodash-es';
import { clientInvitesSync, clientAcceptInviteResult, dismissInviteSuccess } from '../actions';
import { sendAction, INVITE_TOKEN_ID } from '../utils';

interface SagaConfig {
  baseApiUrl: string;
  clientStorePath: string;
  getAuthToken: () => string | undefined;
}

interface AcceptInviteAction {
  type: string;
  payload: {
    userId: string;
    inviteId: string;
  };
}

interface DismissInviteAction {
  type: string;
  payload: {
    inviteToken: string;
  };
}

// Minimal shape of the invites accessed from the client store
interface InviteRecord {
  id: string;
}

// Result shape returned by `sendAction` (untyped .js util)
interface SendActionResult {
  succeeded?: boolean;
  failed?: boolean;
  data?: unknown;
  error?: unknown;
}

export const existingInvitesFromClient =
  (clientStorePath: string) =>
    (state: unknown): InviteRecord[] | undefined =>
      get(state, `${clientStorePath}invites`);

export function* acceptInviteSaga(
  config: SagaConfig,
  action: AcceptInviteAction,
): Generator<unknown, void, unknown> {
  const url = CostCenterServiceUrls.inviteUrl(config.baseApiUrl);
  const { userId, inviteId } = action.payload;
  const serverAction = CloudActions.inviteUserProductAccessAccept(userId, inviteId);
  try {
    const res = (yield call(sendAction, config, serverAction, url)) as SendActionResult;
    const { succeeded, failed, data, error } = res;
    const open = true;
    if (failed) {
      const successFalse = false;
      yield put(clientAcceptInviteResult(open, successFalse, error));
    } else if (succeeded) {
      // then update Redux state needed for the consumer to render a success view in the UI
      // the UI look and feel can be left entirely up to the consumer application
      yield put(clientAcceptInviteResult(open, succeeded, data));
      if (action.payload.inviteId) {
        const { clientStorePath } = config;
        const existingInvites = (yield select(
          existingInvitesFromClient(clientStorePath),
        )) as InviteRecord[];
        // if inviteId included in the client's action (the user is logged in)
        // remove the invite from the stored invites array
        const filteredInvites = existingInvites.filter((i) => i.id !== action.payload.inviteId);
        yield put(clientInvitesSync(filteredInvites));
      }
    }
  } catch (error) {
    const open = true;
    const success = false;
    yield put(clientAcceptInviteResult(open, success, error));
  }
}

export function* dismissInviteSaga(
  action: DismissInviteAction,
): Generator<unknown, void, unknown> {
  const declinedInvites =
    (Lockr.get(INVITE_TOKEN_ID) as Record<string, boolean> | undefined) || {};
  Lockr.set(INVITE_TOKEN_ID, { ...declinedInvites, [action.payload.inviteToken]: true });

  yield put(dismissInviteSuccess(action.payload.inviteToken));
}
